import type { DeviceStatus, SessionStatus, ISODateString } from '@opsrev/shared';

/** Human-friendly relative time, falling back to absolute for older dates. */
export function relativeTime(iso: ISODateString | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function absoluteTime(iso: ISODateString | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/** mm:ss (or h:mm:ss) from a millisecond duration. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

/** Same as formatDuration but accepts seconds (for video timeline). */
export function formatClock(sec: number): string {
  return formatDuration(sec * 1000);
}

export const DEVICE_STATUS_LABEL: Record<DeviceStatus, string> = {
  installed: 'Installed',
  active: 'Active',
  idle: 'Idle',
  offline: 'Offline',
};

export const SESSION_STATUS_LABEL: Record<SessionStatus, string> = {
  uploaded: 'Uploaded',
  processing: 'Processing',
  done: 'Done',
  error: 'Error',
};
